import { useInput } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

import { FileHostingButtons } from "./FileHostingButtons";

export type AttachmentLink = { label: string; url: string };

/**
 * Links to files hosted elsewhere.
 *
 * The Supabase bucket is on a free plan, so large files do not belong in it.
 * Rather than enforcing a size limit and rejecting uploads after the fact, this
 * asks for a link and says where to put the file -- the suggestions come from
 * hub configuration, so they can be changed without a deploy.
 *
 * Stored as jsonb: [{ label, url }]. A row with no url is dropped on save, so
 * an empty pair left behind by a mis-click does not persist.
 */
export function AttachmentLinksInput({
  source = "attachment_links",
  label = "Attachments (links)",
}: {
  source?: string;
  label?: string;
}) {
  const { field } = useInput({ source, defaultValue: [] });

  const links: AttachmentLink[] = Array.isArray(field.value) ? field.value : [];

  const update = (next: AttachmentLink[]) =>
    field.onChange(next.filter((l) => l.url?.trim() || l.label?.trim()));

  const setAt = (index: number, patch: Partial<AttachmentLink>) =>
    update(links.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  return (
    <div className="w-full space-y-2">
      <div className="space-y-2">
        <label className="text-sm font-medium">{label}</label>
        <FileHostingButtons />
        <p className="text-xs text-muted-foreground">
          Set the share link to “anyone with the link can view”, or nobody else
          will be able to open it.
        </p>
      </div>

      <div className="space-y-2">
        {links.map((link, index) => (
          <div key={index} className="flex gap-2">
            <Input
              value={link.label ?? ""}
              onChange={(e) => setAt(index, { label: e.target.value })}
              placeholder="Description"
              className="w-1/3"
            />
            <Input
              value={link.url ?? ""}
              onChange={(e) => setAt(index, { url: e.target.value })}
              placeholder="https://…"
              type="url"
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove link"
              onClick={() => update(links.filter((_, i) => i !== index))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => field.onChange([...links, { label: "", url: "" }])}
      >
        <Plus className="size-4" />
        Add a link
      </Button>
    </div>
  );
}
